import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { components as selectComponents } from 'react-select';
import { Accordion, Card } from 'react-bootstrap';
import moment from 'moment';
import PageHeader from '../../components/common/PageHeader';
import DatePicker from '../../components/UI/DatePicker';
import EmptyData from '../../components/UI/EmptyData';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import { useGetEmployeeDropDownQuery } from '../../redux/apis/employee';
import { useGetAttendanceTimeLogQuery } from '../../redux/apis/attendanceApi';

import { HEADER_TITLE } from '../../utils/constant/header';
import { GLOBAL, ROLES } from '../../utils/constant/global';
import { skipRow } from '../../utils/handler';
import {
  buildAttendanceTimelineSegments,
  buildDailyBreakdownForUser,
  buildHrxOverviewSegments,
  chartDataHasActiveSession,
  collectEmployeesFromTimeLogData,
  computeShiftRemainingMs,
  SHIFT_8H_MS,
  durationToMs,
  fmtHmsClock,
  fmtHmsClockMs,
  fmtShort,
  fmtShortLive,
  parseTeamChartForEmployees,
  TIMELINE_WORK_GRADIENT,
  transformAttendanceTimeLogToChartData,
  getHrxDisplayCheckOutTime,
  isHrxCheckedOut,
  layoutShiftBarSegments,
  snapObsWorkToCheckIn
} from '../../utils/attendanceTimelineHelpers';

import './AttendanceOverview.scss';
import './AttendanceTimeTracking.scss';

const { ATTENDANCE_OVERVIEW } = HEADER_TITLE;
const { LOADING } = GLOBAL;
const PERIODS = ['Day', 'Week', 'Month'];
const DROPDOWN_PAGE_SIZE = 50;
const EMPLOYEE_SEARCH_DEBOUNCE_MS = 400;
const TABLE_PAGE_SIZE = 5;

const getPagerPages = (current, total) => {
  if (total <= 1) return [1];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  let prev = 0;

  sorted.forEach((page) => {
    if (prev && page - prev > 1) result.push('...');
    result.push(page);
    prev = page;
  });

  return result;
};

const getEmployeeDisplayName = (e) =>
  e?.name?.trim() || `${e?.firstName || ''} ${e?.lastName || ''}`.trim() || e?.email || 'Employee';

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const AVATAR_TONES = [
  { color: '#484c7f', background: '#eef0f8' },
  { color: '#6d28d9', background: '#f3e8ff' },
  { color: '#1d4ed8', background: '#eff6ff' },
  { color: '#0f766e', background: '#ccfbf1' },
  { color: '#b45309', background: '#ffedd5' },
  { color: '#be123c', background: '#ffe4e6' }
];

const avatarTone = (name) => {
  const value = String(name || '');
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash + value.charCodeAt(i) * (i + 1)) % AVATAR_TONES.length;
  }
  return AVATAR_TONES[hash];
};

const SEARCH_DEBOUNCE_MS = 400;

/** Preserve picked calendar Y-M-D when building UTC API range (avoids IST/previous-month shift). */
const toUtcPeriodRange = (value, periodType) => {
  // parseZone keeps the originating offset's calendar fields, so a value like
  // "2026-05-01T00:00:00+05:30" reads as May in any browser timezone (no shift).
  const m = moment.parseZone(value);
  if (!m.isValid()) return null;

  if (periodType === 'Day') {
    return {
      start: moment.utc({
        year: m.year(),
        month: m.month(),
        date: m.date(),
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      }),
      end: moment.utc({
        year: m.year(),
        month: m.month(),
        date: m.date(),
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 99
      })
    };
  }

  if (periodType === 'Week') {
    const weekStart = m.clone().startOf('week');
    const weekEnd = m.clone().endOf('week');
    return {
      start: moment.utc({
        year: weekStart.year(),
        month: weekStart.month(),
        date: weekStart.date(),
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      }),
      end: moment.utc({
        year: weekEnd.year(),
        month: weekEnd.month(),
        date: weekEnd.date(),
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 99
      })
    };
  }

  const daysInMonth = m.daysInMonth();
  return {
    start: moment.utc({
      year: m.year(),
      month: m.month(),
      date: 1,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    }),
    end: moment.utc({
      year: m.year(),
      month: m.month(),
      date: daysInMonth,
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 99
    })
  };
};

const initialState = {
  selectType: { type: 'Day', currentDate: moment() },
  search: ''
};

/** Same timeline bar as Attendance Time Tracking (`att-tt-timeline`). */
// eslint-disable-next-line no-unused-vars
const AttendanceTimelineBar = ({
  spanStartMs,
  spanEndMs,
  shiftEndMs,
  workIntervals,
  hrxRow,
  variant,
  isLive,
  emptyLabel = 'No data',
  showRange = true,
  minGapMs = 60 * 1000
}) => {
  const workColor = TIMELINE_WORK_GRADIENT[variant] || TIMELINE_WORK_GRADIENT.obs;
  const now = Date.now();
  const segs =
    variant === 'hrx' && hrxRow
      ? buildHrxOverviewSegments(spanStartMs, spanEndMs, hrxRow, now, workColor, {
          minGapMs,
          minBreakWidthPct: 0.004
        })
      : buildAttendanceTimelineSegments(
          spanStartMs,
          spanEndMs,
          (workIntervals || []).map((w) => ({
            timelogs: {
              start: w.startMs,
              end: w.isRunning ? undefined : w.endMs
            }
          })),
          now,
          workColor,
          {
            includeLeadingBreaks: false,
            includeTrailingBreaks: false,
            minGapMs,
            minBreakWidthPct: 0.004
          }
        );

  if (!segs.length) {
    return <span className="att-ov-empty">{emptyLabel}</span>;
  }

  const variantLabel = variant === 'obs' ? 'Observant' : 'HRX';
  const span = Number(spanEndMs) - Number(spanStartMs);
  const shiftMarkPct =
    shiftEndMs && span > 0 && Number(spanEndMs) > Number(shiftEndMs)
      ? Math.min(100, Math.max(0, ((Number(shiftEndMs) - Number(spanStartMs)) / span) * 100))
      : null;

  return (
    <div className="att-tt-timeline" aria-label={`${variantLabel} timeline`}>
      <div className="att-tt-timeline__bar att-tt-timeline__bar--shift" aria-hidden>
        {shiftMarkPct != null ? (
          <span
            className="att-ov-timeline__shift-mark"
            style={{ left: `${shiftMarkPct}%` }}
            title="8 hour shift end"
          />
        ) : null}
        {segs.slice(0, 80).map((s, idx, arr) => {
          const isFirst = idx === 0;
          const isLast = idx === arr.length - 1;
          const roundClass =
            (isFirst ? ' att-tt-timeline__seg--start' : '') +
            (isLast ? ' att-tt-timeline__seg--end' : '');

          const isBreak = s.kind === 'break';

          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`seg-${idx}`}
              className={`att-tt-timeline__seg${isBreak ? ' att-tt-timeline__seg--break att-ov-timeline__seg--break' : ''}${roundClass}`}
              style={{
                left: `${s.left * 100}%`,
                width: `${s.width * 100}%`,
                background: isBreak ? undefined : s.color
              }}
              title={
                isBreak
                  ? `Break • ${fmtShort(s.startMs)} – ${fmtShort(s.endMs)}`
                  : `${variantLabel} • ${fmtShort(s.startMs)} – ${
                      s.isRunning ? 'Now' : fmtShort(s.endMs)
                    }`
              }
              aria-label={
                isBreak ? `Break from ${fmtShort(s.startMs)} to ${fmtShort(s.endMs)}` : undefined
              }
            />
          );
        })}
      </div>
      {showRange ? (
        <div className="att-tt-timeline__range" aria-hidden>
          <span>{fmtShort(spanStartMs)}</span>
          <span>{fmtShortLive(spanEndMs, isLive)}</span>
        </div>
      ) : null}
    </div>
  );
};

const ShiftProgressBar = ({
  spanStartMs,
  workIntervals,
  workedMs,
  hrxRow,
  variant,
  ariaLabel,
  checkInTime
}) => {
  const start = Number(spanStartMs);
  const end = Number(spanStartMs) + SHIFT_8H_MS;
  const fill = TIMELINE_WORK_GRADIENT[variant] || TIMELINE_WORK_GRADIENT.obs;

  // 8h bar: work/break on the left (clock order); right edge blank = Obs/HRX Pending duration.
  const now = Date.now();
  const obsIntervals =
    variant === 'obs'
      ? snapObsWorkToCheckIn(workIntervals, checkInTime, spanStartMs)
      : workIntervals || [];

  const baseSegs =
    variant === 'hrx' && hrxRow
      ? buildHrxOverviewSegments(start, end, hrxRow, now, fill, {
          minGapMs: 60 * 1000,
          minBreakWidthPct: 0.004
        })
      : buildAttendanceTimelineSegments(
          start,
          end,
          obsIntervals.map((w) => ({
            timelogs: { start: w.startMs, end: w.isRunning ? undefined : w.endMs }
          })),
          now,
          fill,
          {
            includeLeadingBreaks: false,
            includeTrailingBreaks: false,
            minGapMs: 60 * 1000,
            minBreakWidthPct: 0.004
          }
        );

  const pendingMs = computeShiftRemainingMs(durationToMs(workedMs), SHIFT_8H_MS);
  const segs = layoutShiftBarSegments(baseSegs, pendingMs, SHIFT_8H_MS);

  return (
    <div className="att-ov-progress" aria-label={ariaLabel}>
      <div className="att-ov-progress__track" aria-hidden>
        {segs.slice(0, 120).map((s, idx, arr) => {
          const isFirst = idx === 0;
          const isLast = idx === arr.length - 1;
          const roundClass =
            (isFirst ? ' att-ov-progress__seg--start' : '') +
            (isLast ? ' att-ov-progress__seg--end' : '');
          const isBreak = s.kind === 'break';
          const isPending = s.kind === 'pending';
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`seg-${idx}`}
              className={`att-ov-progress__seg${isPending ? ' att-ov-progress__seg--pending' : ' att-ov-progress__seg--work'}${
                isBreak ? ' att-ov-progress__seg--break' : ''
              }${roundClass}`}
              style={{
                left: `${s.left * 100}%`,
                width: `${s.width * 100}%`,
                background: isBreak || isPending ? undefined : s.color
              }}
              title={
                isPending
                  ? `Pending work • ${fmtHmsClockMs(pendingMs)}`
                  : isBreak
                    ? `Break • ${fmtShort(s.startMs)} – ${fmtShort(s.endMs)}`
                    : `${variant === 'obs' ? 'Observant' : 'HRX'} • ${fmtShort(s.startMs)} – ${
                        s.isRunning ? 'Now' : fmtShort(s.endMs)
                      }`
              }
            />
          );
        })}
      </div>
    </div>
  );
};

const DayEmptyState = ({ isWeekend }) => (
  <div className="att-ov-day-empty" role="status">
    <i className={`bi ${isWeekend ? 'bi-calendar2-week' : 'bi-calendar-x'}`} aria-hidden />
    <span>{isWeekend ? 'Weekend — no attendance' : 'No attendance recorded'}</span>
  </div>
);

const DayTimelineCompare = ({
  spanStartMs,
  // spanEndMs,
  // shiftEndMs,
  checkInTime,
  obsWorkIntervals,
  hrxWorkIntervals,
  hrxRow,
  observantMilliseconds,
  hrxMilliseconds,
  hasHrx,
  hasObs,
  isObsRunning,
  isHrxActive
  // isSessionActive,
  // liveNow
}) => {
  const hasObsWork = hasObs && Number(observantMilliseconds) > 0;

  return (
    <div className="att-ov-compare">
      <div className="att-ov-compare__row att-ov-compare__row--obs">
        <span className="att-ov-compare__label">Observant</span>
        <div className="att-ov-compare__bar">
          {hasObsWork ? (
            <ShiftProgressBar
              spanStartMs={spanStartMs}
              workIntervals={obsWorkIntervals}
              workedMs={observantMilliseconds}
              variant="obs"
              checkInTime={checkInTime}
              ariaLabel="Observant 8 hour progress"
            />
          ) : (
            <div className="att-ov-progress att-ov-progress--empty" aria-hidden>
              <div className="att-ov-progress__track att-ov-progress__track--empty" />
            </div>
          )}
        </div>
        <span
          className={`att-ov-compare__value${isObsRunning ? ' att-ov-compare__value--live' : ''}${!hasObsWork ? ' att-ov-compare__value--none' : ''}`}
        >
          {hasObsWork ? fmtHmsClock(observantMilliseconds) : 'No record'}
        </span>
      </div>

      <div className="att-ov-compare__row att-ov-compare__row--hrx">
        <span className="att-ov-compare__label">HRX</span>
        <div className="att-ov-compare__bar">
          {hasHrx ? (
            <ShiftProgressBar
              spanStartMs={spanStartMs}
              workIntervals={hrxWorkIntervals}
              workedMs={hrxMilliseconds}
              hrxRow={hrxRow}
              variant="hrx"
              checkInTime={checkInTime}
              ariaLabel="HRX 8 hour progress"
            />
          ) : (
            <div className="att-ov-progress att-ov-progress--empty" aria-hidden>
              <div className="att-ov-progress__track att-ov-progress__track--empty" />
            </div>
          )}
        </div>
        <span
          className={`att-ov-compare__value${isHrxActive ? ' att-ov-compare__value--live' : ''}${!hasHrx ? ' att-ov-compare__value--none' : ''}`}
        >
          {hasHrx ? fmtHmsClock(hrxMilliseconds) : 'No record'}
        </span>
      </div>
    </div>
  );
};

const StatPill = ({ label, value, variant }) => (
  <div className={`att-ov-pill att-ov-pill--${variant}`}>
    <span className="att-ov-pill__label">{label}</span>
    <span className="att-ov-pill__value">{value}</span>
  </div>
);

const DayStat = ({ label, value, className = '' }) => (
  <div className={`att-ov-stat${className ? ` ${className}` : ''}`}>
    <span className="att-ov-stat__copy">
      <span className="att-ov-stat__label">{label}</span>
      <b className="att-ov-stat__value">{value}</b>
    </span>
  </div>
);

const getHrxStatusVariant = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('checked in')) return 'checked-in';
  if (s.includes('on break')) return 'on-break';
  if (s.includes('checked out')) return 'checked-out';
  return 'default';
};

const DayDateBadge = ({ ymd, label, isWeekend }) => {
  const dayMoment = moment.utc(ymd, 'YYYY-MM-DD', true);
  const fallbackDow = label.split(',')?.[0] || label;
  const fallbackDmy = (label.split(',')?.[1] || '').trim();

  const dow = dayMoment.isValid() ? dayMoment.format('ddd') : fallbackDow;
  const dayNum = dayMoment.isValid() ? dayMoment.format('DD') : fallbackDmy.split(' ')?.[0] || '—';
  const month = dayMoment.isValid() ? dayMoment.format('MMM') : fallbackDmy.split(' ')?.[1] || '';

  return (
    <div className={`att-ov-date-badge${isWeekend ? ' att-ov-date-badge--weekend' : ''}`}>
      <span className="att-ov-date-badge__dow">{dow}</span>
      <span className="att-ov-date-badge__day">{dayNum}</span>
      <span className="att-ov-date-badge__month">{month}</span>
      {isWeekend ? <span className="att-ov-date-badge__tag">Weekend</span> : null}
    </div>
  );
};

function AttendanceOverview() {
  const [state, setState] = useState(initialState);
  const [fromDate, setFromDate] = useState(() =>
    moment.utc().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format()
  );
  const [toDate, setToDate] = useState(() =>
    moment.utc().set({ hour: 23, minute: 59, second: 59, millisecond: 99 }).format()
  );
  const [employees, setEmployees] = useState([]);
  const [dropdownPage, setDropdownPage] = useState(1);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [rows, setRows] = useState([]);
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const lastProcessedDropdownRef = useRef(null);
  const employeeSearchTimerRef = useRef(null);
  const appliedEmployeeSearchRef = useRef('');
  /** Every employee seen so far (survives search resets) so the selected one keeps its name. */
  const employeesRef = useRef([]);

  const { data: authData } = useIsLoggedInQuery();
  const roleName = authData?.data?.hrxRole?.roleName;
  const canViewTeam =
    roleName === ROLES.ADMIN || roleName === ROLES.HR || roleName === ROLES.ACCOUNTANT;

  const {
    data: dropdownData,
    isFetching: dropdownFetching,
    refetch: employeeDropDownQuery
  } = useGetEmployeeDropDownQuery(
    {
      skip: skipRow(dropdownPage, DROPDOWN_PAGE_SIZE),
      limit: DROPDOWN_PAGE_SIZE,
      search: dropdownSearch,
      sort: 'name',
      sortType: 'asc'
    },
    { skip: !canViewTeam }
  );

  // RTK refetch throws if the query was never started; never let that crash the page
  const safeRefetchEmployees = () => {
    try {
      employeeDropDownQuery?.();
    } catch (err) {
      console.error('Employee dropdown refetch failed:', err);
    }
  };

  useEffect(() => {
    if (!canViewTeam || dropdownFetching) return;
    if (!dropdownData || !Array.isArray(dropdownData.data)) return;
    if (lastProcessedDropdownRef.current === dropdownData) return;
    lastProcessedDropdownRef.current = dropdownData;

    const seen = new Map(employeesRef.current.map((e) => [e._id, e]));
    dropdownData.data.forEach((e) => {
      if (e?._id) seen.set(e._id, e);
    });
    employeesRef.current = Array.from(seen.values());

    setEmployees((prev) => {
      // Page 1 is a fresh list (first load or new search); later pages append
      const base = dropdownPage === 1 ? [] : prev;
      const byId = new Map();
      [...base, ...dropdownData.data].forEach((e) => {
        if (e?._id) byId.set(e._id, e);
      });
      return Array.from(byId.values());
    });
  }, [canViewTeam, dropdownData, dropdownFetching, dropdownPage]);

  // Refetch after the page/search arg has updated so every scroll hits the API
  const dropdownFirstRunRef = useRef(true);
  useEffect(() => {
    if (dropdownFirstRunRef.current) {
      dropdownFirstRunRef.current = false;
      return;
    }
    if (canViewTeam) safeRefetchEmployees();
  }, [dropdownPage, dropdownSearch]);

  /** Returns true when the next page was requested. */
  const handleMenuScrollToBottom = () => {
    const pageRows = Array.isArray(dropdownData?.data) ? dropdownData.data.length : 0;
    // A full page also counts as "more", in case isNext is missing or wrong
    const hasMore = Boolean(dropdownData?.isNext) || pageRows >= DROPDOWN_PAGE_SIZE;
    if (!hasMore || dropdownFetching) return false;
    setDropdownPage((p) => p + 1);
    return true;
  };

  // react-select's onMenuScrollToBottom only reacts to wheel/touch, so also listen to the
  // list's native scroll. The ref keeps the memoized component fresh.
  const scrollToBottomRef = useRef(handleMenuScrollToBottom);
  scrollToBottomRef.current = handleMenuScrollToBottom;
  const employeeMenuListComponents = useMemo(() => {
    let armed = true;
    const EmployeeMenuList = (props) => (
      <selectComponents.MenuList
        {...props}
        innerProps={{
          ...props.innerProps,
          onScroll: (e) => {
            const el = e?.currentTarget;
            if (!el) return;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
            if (!nearBottom) {
              armed = true;
            } else if (armed) {
              armed = !scrollToBottomRef.current?.();
            }
          }
        }}
      />
    );
    return { MenuList: EmployeeMenuList };
  }, []);

  useEffect(
    () => () => {
      if (employeeSearchTimerRef.current) clearTimeout(employeeSearchTimerRef.current);
    },
    []
  );

  // Employee search is server-side: debounce typing, then reset the paged list
  const applyEmployeeSearch = (term) => {
    if (appliedEmployeeSearchRef.current === term) return;
    appliedEmployeeSearchRef.current = term;
    setDropdownSearch(term);
    setDropdownPage(1);
  };

  const handleEmployeeSearchInput = (text, meta) => {
    if (employeeSearchTimerRef.current) clearTimeout(employeeSearchTimerRef.current);
    if (meta?.action !== 'input-change') {
      // Selected / menu closed / blurred: drop the search so the full list comes back
      applyEmployeeSearch('');
      return;
    }
    employeeSearchTimerRef.current = setTimeout(() => {
      applyEmployeeSearch(String(text || '').trim());
    }, EMPLOYEE_SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(state.search.trim());
      setTablePage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [state.search]);

  const employeeOptions = useMemo(() => {
    const list = [...employees];
    // Keep the selected employee in the options even when the current search hides them
    if (selectedUserId && !list.some((e) => e._id === selectedUserId)) {
      const seen = employeesRef.current.find((e) => e._id === selectedUserId);
      if (seen) list.unshift(seen);
    }
    return [
      { value: null, label: 'All employees' },
      ...list.map((e) => ({
        value: e._id,
        label: getEmployeeDisplayName(e)
      }))
    ];
  }, [employees, selectedUserId]);

  const selectedEmployeeOption = useMemo(
    () => employeeOptions.find((o) => o.value === selectedUserId) ?? employeeOptions[0],
    [employeeOptions, selectedUserId]
  );

  const timeLogQueryParams = useMemo(() => {
    const params = {
      startDate: moment.utc(fromDate).toISOString(),
      endDate: moment.utc(toDate).toISOString(),
      page: tablePage,
      limit: TABLE_PAGE_SIZE
    };
    if (selectedUserId) {
      params.userId = selectedUserId;
    } else if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    return params;
  }, [fromDate, toDate, tablePage, rowsPerPage, debouncedSearch, selectedUserId]);

  const {
    data: timeLogResponse,
    isFetching: chartFetching,
    isLoading: chartLoading
  } = useGetAttendanceTimeLogQuery(timeLogQueryParams, {
    skip: !canViewTeam,
    refetchOnMountOrArgChange: true
  });

  const chartData = useMemo(
    () => transformAttendanceTimeLogToChartData(timeLogResponse),
    [timeLogResponse]
  );

  const pagination = timeLogResponse?.pagination;
  const totalPages = Math.max(1, Number(pagination?.totalPages) || 1);
  const totalRecords = Number(pagination?.totalRecords) || 0;
  const rangeStart = totalRecords ? (tablePage - 1) * TABLE_PAGE_SIZE + 1 : 0;
  const rangeEnd = totalRecords ? Math.min(tablePage * TABLE_PAGE_SIZE, totalRecords) : 0;
  const pagerPages = getPagerPages(tablePage, totalPages);
  const pagerLoading = chartLoading || chartFetching;

  const hasActiveSession = useMemo(() => chartDataHasActiveSession(chartData), [chartData]);

  const [liveNow, setLiveNow] = useState(() => Date.now());

  useEffect(() => {
    if (!hasActiveSession) return undefined;
    setLiveNow(Date.now());
    const timer = setInterval(() => setLiveNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [hasActiveSession, chartData]);

  useEffect(() => {
    if (!timeLogResponse?.data) {
      setRows([]);
      return;
    }

    let target = collectEmployeesFromTimeLogData(timeLogResponse.data);
    if (selectedUserId && !target.length) {
      const found = employeesRef.current.find((e) => e._id === selectedUserId);
      const label = selectedEmployeeOption?.label;
      target = found
        ? [found]
        : [
            {
              _id: selectedUserId,
              name: label && label !== 'All employees' ? label : '',
              email: ''
            }
          ];
    }
    if (!target.length) {
      setRows([]);
      return;
    }

    const results = parseTeamChartForEmployees(
      chartData,
      target,
      state.selectType.type,
      moment.utc(fromDate),
      moment.utc(toDate),
      liveNow
    );
    setRows(results);
  }, [
    chartData,
    timeLogResponse,
    selectedUserId,
    selectedEmployeeOption,
    state.selectType.type,
    fromDate,
    toDate,
    liveNow
  ]);

  const chartLoadingState = chartLoading || chartFetching;

  const employeeRows = useMemo(() => {
    if (!rows?.length) return [];
    return [...rows].sort((a, b) => {
      const ah = (a?.hasHrx ? 1 : 0) + (a?.hasObs ? 1 : 0);
      const bh = (b?.hasHrx ? 1 : 0) + (b?.hasObs ? 1 : 0);
      return bh - ah;
    });
  }, [rows]);

  const handleDateSelection = (type) => {
    const src = state.selectType.currentDate || moment();
    // Rebuild from literal calendar fields so switching period can't shift the
    // anchor across a timezone boundary (e.g. May -> April on a non-IST browser).
    const value = moment({ year: src.year(), month: src.month(), date: src.date() }).startOf('day');
    const range = toUtcPeriodRange(value, type);
    if (!range) return;

    setRows([]);
    setState((s) => ({ ...s, selectType: { type, currentDate: value } }));
    setFromDate(range.start.format());
    setToDate(range.end.format());
    setTablePage(1);
  };

  const handleDateSelectionRange = (value) => {
    const type = state.selectType.type;
    const m = moment(value);
    // Anchor to the picked calendar fields (DatePicker already normalizes these),
    // never re-interpreting the instant in the browser's timezone.
    const picked =
      type === 'Month'
        ? moment({ year: m.year(), month: m.month(), date: 1 }).startOf('day')
        : moment({ year: m.year(), month: m.month(), date: m.date() }).startOf('day');
    const range = toUtcPeriodRange(picked, type);
    if (!range) return;

    setRows([]);
    setFromDate(range.start.format());
    setToDate(range.end.format());
    setState((s) => ({ ...s, selectType: { ...s.selectType, currentDate: picked } }));
    setTablePage(1);
  };

  const handleUserSelect = (opt) => {
    const userId = opt?.value ?? null;
    setRows([]);
    setTablePage(1);
    if (userId) {
      setState((s) => ({ ...s, search: '' }));
      setDebouncedSearch('');
    }
    setSelectedUserId(userId);
  };

  if (!canViewTeam) {
    return (
      <div className="att-ov-page">
        <PageHeader headerTitle={ATTENDANCE_OVERVIEW} />
        <Card className="att-ov-card border-0 shadow-sm">
          <Card.Body>
            <EmptyData message="This page is available for Admin and HR only." />
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="att-ov-page">
      <PageHeader headerTitle={ATTENDANCE_OVERVIEW} />

      <Card className="att-ov-card border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="att-ov-toolbar">
            <div className="att-ov-toolbar__filters">
              <div className="att-ov-field att-ov-field--user">
                <label className="att-ov-field__label" htmlFor="att-ov-user">
                  Employee
                </label>
                <Select
                  inputId="att-ov-user"
                  classNamePrefix="att-ov-select"
                  className="att-ov-select-wrap"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  options={employeeOptions}
                  value={selectedEmployeeOption}
                  onChange={handleUserSelect}
                  filterOption={null}
                  isLoading={dropdownFetching}
                  onInputChange={handleEmployeeSearchInput}
                  components={employeeMenuListComponents}
                  isClearable={false}
                  placeholder="All employees"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
              </div>

              <div className="att-ov-period">
                <span className="att-ov-field__label">Period</span>
                <div className="att-ov-tabs" role="tablist" aria-label="Period">
                  {PERIODS.map((period) => (
                    <button
                      key={period}
                      type="button"
                      role="tab"
                      aria-selected={state.selectType.type === period}
                      className={`att-ov-tab${state.selectType.type === period ? ' att-ov-tab--active' : ''}`}
                      onClick={() => handleDateSelection(period)}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>

              <div
                className={`att-ov-field att-ov-field--date att-ov-field--${state.selectType.type.toLowerCase()}`}
              >
                <label className="att-ov-field__label">Date</label>
                <div className="att-ov-date">
                  <i className="bi bi-calendar3" aria-hidden />
                  <DatePicker
                    initialDate={state.selectType.currentDate}
                    type={state.selectType.type}
                    handleChangeDate={handleDateSelectionRange}
                  />
                </div>
              </div>
            </div>

            <div className="att-ov-toolbar__search">
              <i className="bi bi-search" aria-hidden />
              <input
                type="search"
                className="form-control"
                placeholder="Search by name or email…"
                value={state.search}
                onChange={(e) => setState((s) => ({ ...s, search: e.target.value }))}
                aria-label="Search employees"
              />
            </div>
          </div>

          <div className="att-ov-legend" aria-hidden>
            <span>
              <i className="att-ov-legend__swatch att-ov-legend__swatch--obs" /> Observant work time
            </span>
            <span>
              <i className="att-ov-legend__swatch att-ov-legend__swatch--hrx" /> HRX work time
            </span>
            <span>
              <i className="att-ov-legend__swatch att-ov-legend__swatch--break" /> Break
            </span>
            <span>
              <i className="att-ov-legend__swatch att-ov-legend__swatch--pending" /> Pending
            </span>
          </div>

          {chartLoadingState ? (
            <div className="p-4 text-center text-muted">{LOADING}</div>
          ) : !employeeRows.length ? (
            <div className="p-3">
              <EmptyData
                message={
                  selectedUserId
                    ? 'No attendance data for this employee on the selected date.'
                    : 'No attendance data for this period.'
                }
              />
            </div>
          ) : (
            <div className="att-ov-accordion-wrap">
              <Accordion defaultActiveKey={String(employeeRows?.[0]?.id || 0)}>
                {employeeRows.map((row, idx) => {
                  const e = row.employee;
                  const name = getEmployeeDisplayName(e);

                  const daily = buildDailyBreakdownForUser(
                    chartData,
                    row.id,
                    moment.utc(fromDate),
                    moment.utc(toDate),
                    liveNow
                  );

                  return (
                    <Accordion.Item
                      key={row.id || `emp-${idx}`}
                      eventKey={String(row.id || idx)}
                      className="att-ov-accordion-item"
                    >
                      <Accordion.Header>
                        <div className="att-ov-acc-head">
                          <div className="att-ov-employee">
                            <span
                              className="att-ov-employee__avatar"
                              style={avatarTone(name)}
                              aria-hidden
                            >
                              {getInitials(name)}
                            </span>
                            <span className="att-ov-employee__meta">
                              <span className="att-ov-employee__name">{name}</span>
                              {e?.email ? (
                                <span className="att-ov-employee__email">{e.email}</span>
                              ) : null}
                            </span>
                          </div>

                          <div className="att-ov-acc-metrics">
                            <StatPill
                              label="OBS"
                              value={fmtHmsClock(row.observantMilliseconds)}
                              variant="obs"
                            />
                            <StatPill
                              label="HRX"
                              value={row.hasHrx ? fmtHmsClock(row.hrxMilliseconds) : 'No record'}
                              variant="hrx"
                            />
                          </div>
                        </div>
                      </Accordion.Header>

                      <Accordion.Body>
                        <div className="att-ov-acc-body">
                          <div className="att-ov-day-rows">
                            {daily.map((d) => {
                              const hasDayData = d.hasObs || d.hasHrx;
                              const hrxStatus = d.hrxRow?.currentStatus;
                              const isWeekend = (() => {
                                const m = moment.utc(d.ymd, 'YYYY-MM-DD', true);
                                if (!m.isValid()) return false;
                                const day = m.day(); // 0=Sun ... 6=Sat
                                return day === 0 || day === 6;
                              })();
                              return (
                                <div
                                  key={d.ymd}
                                  className={`att-ov-day-row att-ov-day-row--list${hasDayData ? ' att-ov-day-row--has-data' : ' att-ov-day-row--no-data'}${isWeekend ? ' att-ov-day-row--weekend' : ''}`}
                                >
                                  <DayDateBadge ymd={d.ymd} label={d.label} isWeekend={isWeekend} />

                                  <div className="att-ov-day-row__content">
                                    {!hasDayData ? (
                                      <DayEmptyState isWeekend={isWeekend} />
                                    ) : (
                                      <>
                                        <DayTimelineCompare
                                          spanStartMs={d.timeline?.start}
                                          spanEndMs={d.timeline?.end}
                                          shiftEndMs={d.timeline?.shiftEndMs}
                                          checkInTime={d.checkInTime}
                                          obsWorkIntervals={d.obsWorkIntervals}
                                          hrxWorkIntervals={d.hrxWorkIntervals}
                                          hrxRow={d.hrxRow}
                                          observantMilliseconds={d.observantMilliseconds}
                                          hrxMilliseconds={d.hrxMilliseconds}
                                          hasHrx={d.hasHrx}
                                          hasObs={d.hasObs}
                                          isObsRunning={d.isObsRunning}
                                          isHrxActive={d.isHrxActive}
                                          isSessionActive={d.isSessionActive}
                                          liveNow={liveNow}
                                        />

                                        <div className="att-ov-day-row__foot att-ov-day-row__foot--compare">
                                          <span
                                            className="att-ov-day-row__foot-spacer"
                                            aria-hidden
                                          />
                                          <div className="att-ov-day-row__foot-meta">
                                            {d.checkInTime ? (
                                              <DayStat
                                                label="Check-in"
                                                value={fmtShort(d.checkInTime)}
                                              />
                                            ) : null}
                                            {isHrxCheckedOut(d.hrxRow) ? (
                                              <DayStat
                                                label="Check-out"
                                                value={fmtShort(
                                                  getHrxDisplayCheckOutTime(d.hrxRow)
                                                )}
                                              />
                                            ) : null}
                                            {d.hasHrx && hrxStatus ? (
                                              <DayStat
                                                className={`att-ov-stat--hrx-status att-ov-stat--${getHrxStatusVariant(hrxStatus)}`}
                                                label="HRX Status"
                                                value={hrxStatus}
                                              />
                                            ) : null}
                                            {d.observantBreakMilliseconds > 0 ? (
                                              <DayStat
                                                className="att-ov-stat--obs-break"
                                                label="Obs Break"
                                                value={fmtHmsClockMs(d.observantBreakMilliseconds)}
                                              />
                                            ) : null}
                                            {d.totalBreakMilliseconds > 0 ? (
                                              <DayStat
                                                className="att-ov-stat--hrx-break"
                                                label="HRX Break"
                                                value={fmtHmsClockMs(d.totalBreakMilliseconds)}
                                              />
                                            ) : null}
                                            {(() => {
                                              if (!isHrxCheckedOut(d.hrxRow)) return null;
                                              const worked = Number(d.observantMilliseconds || 0);
                                              if (!worked) return null;
                                              const pending = computeShiftRemainingMs(
                                                worked,
                                                SHIFT_8H_MS
                                              );
                                              if (!pending) return null;
                                              return (
                                                <DayStat
                                                  className="att-ov-stat--pending"
                                                  label="Obs Pending"
                                                  value={fmtHmsClockMs(pending)}
                                                />
                                              );
                                            })()}
                                            {(() => {
                                              if (!d.hasHrx || !isHrxCheckedOut(d.hrxRow))
                                                return null;
                                              const worked = Number(d.hrxMilliseconds || 0);
                                              if (!worked) return null;
                                              const pending = computeShiftRemainingMs(
                                                worked,
                                                SHIFT_8H_MS
                                              );
                                              if (!pending) return null;
                                              return (
                                                <DayStat
                                                  className="att-ov-stat--pending"
                                                  label="HRX Pending"
                                                  value={fmtHmsClockMs(pending)}
                                                />
                                              );
                                            })()}
                                          </div>
                                          <span
                                            className="att-ov-day-row__foot-spacer"
                                            aria-hidden
                                          />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>

              <div className={`att-ov-pager${pagerLoading ? ' att-ov-pager--loading' : ''}`}>
                <div className="att-ov-pager__left">
                  <span className="att-ov-pager__badge">
                    <i className="bi bi-people-fill" aria-hidden />
                    {totalRecords > 0
                      ? `${totalRecords} employee${totalRecords !== 1 ? 's' : ''}`
                      : 'No employees'}
                  </span>
                  {totalRecords > 0 ? (
                    <>
                      <span className="att-ov-pager__divider" aria-hidden />
                      <span className="att-ov-pager__range">
                        Viewing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong>
                      </span>
                    </>
                  ) : null}
                </div>

                {totalPages > 1 ? (
                  <div className="att-ov-pager__right">
                    <span className="att-ov-pager__status">
                      Page <strong>{tablePage}</strong> of <strong>{totalPages}</strong>
                    </span>
                    <span
                      className="att-ov-pager__divider att-ov-pager__divider--compact"
                      aria-hidden
                    />
                    <nav className="att-ov-pager__nav" aria-label="Attendance pagination">
                      <div className="att-ov-pager__track">
                        <button
                          type="button"
                          className="att-ov-pager__arrow att-ov-pager__arrow--edge"
                          onClick={() => setTablePage(1)}
                          disabled={pagerLoading || tablePage <= 1}
                          aria-label="First page"
                          title="First page"
                        >
                          <i className="bi bi-chevron-double-left" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="att-ov-pager__arrow"
                          onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                          disabled={pagerLoading || tablePage <= 1}
                          aria-label="Previous page"
                          title="Previous page"
                        >
                          <i className="bi bi-chevron-left" aria-hidden />
                          <span className="att-ov-pager__arrow-label">Prev</span>
                        </button>

                        <div className="att-ov-pager__pages">
                          {pagerPages.map((page, idx) =>
                            page === '...' ? (
                              <span
                                key={`ellipsis-${idx}`}
                                className="att-ov-pager__ellipsis"
                                aria-hidden
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={page}
                                type="button"
                                className={`att-ov-pager__page${
                                  tablePage === page ? ' att-ov-pager__page--active' : ''
                                }`}
                                onClick={() => setTablePage(page)}
                                disabled={pagerLoading}
                                aria-current={tablePage === page ? 'page' : undefined}
                              >
                                {page}
                              </button>
                            )
                          )}
                        </div>

                        <button
                          type="button"
                          className="att-ov-pager__arrow"
                          onClick={() => setTablePage((p) => Math.min(totalPages, p + 1))}
                          disabled={pagerLoading || tablePage >= totalPages}
                          aria-label="Next page"
                          title="Next page"
                        >
                          <span className="att-ov-pager__arrow-label">Next</span>
                          <i className="bi bi-chevron-right" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="att-ov-pager__arrow att-ov-pager__arrow--edge"
                          onClick={() => setTablePage(totalPages)}
                          disabled={pagerLoading || tablePage >= totalPages}
                          aria-label="Last page"
                          title="Last page"
                        >
                          <i className="bi bi-chevron-double-right" aria-hidden />
                        </button>
                      </div>
                    </nav>
                    {pagerLoading ? (
                      <span className="att-ov-pager__loading" aria-live="polite">
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden
                        />
                        <span className="visually-hidden">Loading</span>
                      </span>
                    ) : null}
                  </div>
                ) : totalRecords > 0 ? (
                  <span className="att-ov-pager__single">All records on this page</span>
                ) : null}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default AttendanceOverview;
